import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Provider } from '../types/api';
import { providerService } from '../services/providerService';

interface ProviderState {
  billingProviders: Provider[];
  renderingProviders: Provider[];
  supervisingProviders: Provider[];
  loading: {
    billing: boolean;
    rendering: boolean;
    supervising: boolean;
  };
  error: {
    billing: string | null;
    rendering: string | null;
    supervising: string | null;
  };
}

const initialState: ProviderState = {
  billingProviders: [],
  renderingProviders: [],
  supervisingProviders: [],
  loading: {
    billing: false,
    rendering: false,
    supervising: false,
  },
  error: {
    billing: null,
    rendering: null,
    supervising: null,
  },
};

// Async thunks for fetching providers
export const fetchBillingProviders = createAsyncThunk(
  'providers/fetchBillingProviders',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await providerService.getBillingProviders(tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch billing providers';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchRenderingProviders = createAsyncThunk(
  'providers/fetchRenderingProviders',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await providerService.getRenderingProviders(tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch rendering providers';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchSupervisingProviders = createAsyncThunk(
  'providers/fetchSupervisingProviders',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await providerService.getSupervisingProviders(tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch supervising providers';
      return rejectWithValue(errorMessage);
    }
  },
);

const providerSlice = createSlice({
  name: 'providers',
  initialState,
  reducers: {
    clearBillingProviders: (state) => {
      state.billingProviders = [];
      state.error.billing = null;
    },
    clearRenderingProviders: (state) => {
      state.renderingProviders = [];
      state.error.rendering = null;
    },
    clearSupervisingProviders: (state) => {
      state.supervisingProviders = [];
      state.error.supervising = null;
    },
    clearAllProviders: (state) => {
      state.billingProviders = [];
      state.renderingProviders = [];
      state.supervisingProviders = [];
      state.error = {
        billing: null,
        rendering: null,
        supervising: null,
      };
    },
    clearProviderError: (state, action) => {
      const { providerType } = action.payload;
      if (providerType === 'billing') {
        state.error.billing = null;
      } else if (providerType === 'rendering') {
        state.error.rendering = null;
      } else if (providerType === 'supervising') {
        state.error.supervising = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Billing providers
      .addCase(fetchBillingProviders.pending, (state) => {
        state.loading.billing = true;
        state.error.billing = null;
      })
      .addCase(fetchBillingProviders.fulfilled, (state, action) => {
        state.loading.billing = false;
        if (
          JSON.stringify(state.billingProviders) !==
          JSON.stringify(action.payload)
        ) {
          state.billingProviders = action.payload;
        }
      })
      .addCase(fetchBillingProviders.rejected, (state, action) => {
        state.loading.billing = false;
        state.error.billing = action.payload as string;
      })
      // Rendering providers
      .addCase(fetchRenderingProviders.pending, (state) => {
        state.loading.rendering = true;
        state.error.rendering = null;
      })
      .addCase(fetchRenderingProviders.fulfilled, (state, action) => {
        state.loading.rendering = false;
        if (
          JSON.stringify(state.renderingProviders) !==
          JSON.stringify(action.payload)
        ) {
          state.renderingProviders = action.payload;
        }
      })
      .addCase(fetchRenderingProviders.rejected, (state, action) => {
        state.loading.rendering = false;
        state.error.rendering = action.payload as string;
      })
      // Supervising providers
      .addCase(fetchSupervisingProviders.pending, (state) => {
        state.loading.supervising = true;
        state.error.supervising = null;
      })
      .addCase(fetchSupervisingProviders.fulfilled, (state, action) => {
        state.loading.supervising = false;
        if (
          JSON.stringify(state.supervisingProviders) !==
          JSON.stringify(action.payload)
        ) {
          state.supervisingProviders = action.payload;
        }
      })
      .addCase(fetchSupervisingProviders.rejected, (state, action) => {
        state.loading.supervising = false;
        state.error.supervising = action.payload as string;
      });
  },
});

export const {
  clearBillingProviders,
  clearRenderingProviders,
  clearSupervisingProviders,
  clearAllProviders,
  clearProviderError,
} = providerSlice.actions;

export default providerSlice.reducer;
